import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Radio } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addFreezingFishControl,
  FoodControlPointInstanceDto,
  FreezingFish,
  InstanceStatus,
  updateFreezingFishControl,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface FreezingFishControlModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstanceDto;
  initialData?: FreezingFish;
}

export function FreezingFishControlModal({
  foodControlPointInstance,
  initialData,
  ...props
}: FreezingFishControlModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { user } = useAuthStore();

  const [selectedOption, setSelectedOption] = useState(1);
  const freezingFishControlTexts = useMemo(
    () => [
      {
        id: 1,
        text: t("food.FreezingFishControlText1"),
        acceptable: true,
      },
      {
        id: 2,
        text: t("food.FreezingFishControlText2"),
        acceptable: true,
      },
      {
        id: 3,
        text: t("food.FreezingFishControlText3"),
        acceptable: true,
      },
      {
        id: 4,
        text: t("food.FreezingFishControlText4"),
        acceptable: true,
      },
      {
        id: 5,
        text: t("food.FreezingFishControlText5"),
        acceptable: false,
      },
    ],
    [t],
  );
  const freezingFishSchema = useMemo(
    () =>
      z
        .object({
          remark: z.string().optional(),
          note: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          const selected = freezingFishControlTexts.find(
            (item) => item.id === selectedOption,
          );
          if (selected && !selected.acceptable) {
            if (!data.note || data.note.trim().length === 0) {
              ctx.addIssue({
                code: "custom",
                message: t("food.Corrective action is required"),
                path: ["note"],
              });
            }
          }
        }),
    [t, selectedOption, freezingFishControlTexts],
  );

  type FreezingFishData = z.infer<typeof freezingFishSchema>;

  const {
    handleSubmit,
    register,
    reset,
    formState: state,
  } = useForm<FreezingFishData>({
    resolver: zodResolver(freezingFishSchema),
  });

  const updateMutation = useMutation({
    mutationFn: updateFreezingFishControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      setSelectedOption(1);
      toast.success(t("food.Proccessing Product Has Been Updated"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("common.An error occurred"));
    },
  });

  const mutation = useMutation({
    mutationFn: addFreezingFishControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      setSelectedOption(1);
      toast.success(t("food.Proccessing Product Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("common.An error occurred"));
    },
  });

  const onSubmit = useCallback(
    (data: FreezingFishData) => {
      if (!user?.id) return;

      const isCompeletedWithErrors = !freezingFishControlTexts.find(
        (item) => item.id === selectedOption,
      )?.acceptable;

      const payload = {
        companyId: foodControlPointInstance.companyId,
        reason: data.note,
        remark: data.remark,
        status: isCompeletedWithErrors
          ? InstanceStatus.completedWithErrors
          : InstanceStatus.completed,
        registeredBy: user?.id,
        companyLocationId: foodControlPointInstance.companyLocationId,
        controlPointInstanceId: foodControlPointInstance.id,
        selectedTextId: selectedOption,
        foodControlPointInstance,
      };

      if (initialData) {
        updateMutation.mutate(payload);
        return;
      }

      mutation.mutate(payload);
    },
    [
      user?.id,
      foodControlPointInstance,
      initialData,
      mutation,
      updateMutation,
      freezingFishControlTexts,
      selectedOption,
    ],
  );
  useEffect(() => {
    if (initialData) {
      const { remark, reason } = JSON.parse(initialData.note || "{}");
      setSelectedOption(initialData?.selectedTextId);
      reset({
        note: reason,
        remark: remark,
      });
    }
  }, [initialData, reset]);

  return (
    <Modal
      title={t("food.Freezing Fish")}
      {...props}
      actionButtons={
        <>
          <Button
            disabled={mutation.isPending}
            variant="secondary"
            onClick={() => {
              reset();
              props.onClose?.();
            }}
          >
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
    >
      <form>
        <div className="border-b-[1px] border-b-gray-300 pb-2.5">
          {freezingFishControlTexts.map((item) => (
            <div
              key={item.id}
              className="gap-2.5 py-2.5 form-label text-[#252F4A] !font-normal"
            >
              <div className="flex flex-row justify-between w-full">
                {item.text}
                <Radio
                  onClick={() => {
                    setSelectedOption(item.id);
                  }}
                  color={item.acceptable ? "acceptable" : "danger"}
                  checked={selectedOption === item.id}
                />
              </div>
            </div>
          ))}
        </div>

        {!freezingFishControlTexts.find((item) => item.id === selectedOption)
          ?.acceptable && (
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Corrective Action")} required>
              <Input {...register("note")} />
            </Label>
            <ErrorMessage message={state.errors.note?.message} />
          </div>
        )}
        <div>
          <Label title={t("food.Remarks")}>
            <Input {...register("remark")} placeholder="-" />
          </Label>
          <ErrorMessage message={state.errors.remark?.message} />
        </div>
      </form>
    </Modal>
  );
}
